import { type Node, type NodeChange } from '@xyflow/react';
import { useCallback, useRef } from 'react';

/** What one batch of node changes means for a phase cascade. */
export interface PhaseGestureFrame<TNode extends Node> {
  /**
   * The nodes when the current drag or resize started. For a change outside a
   * gesture, the nodes before the change. Give these to `cascadePhases` as
   * `baselineNodes`.
   */
  baselineNodes: TNode[];
  /**
   * True when the changes can carry a node out of its phase: a move, a
   * `replace`, or a resize. False for the measurement that React Flow reports
   * when it first renders a node, so that a saved diagram does not move when
   * it opens.
   */
  canMovePhases: boolean;
  /** True for the first frame of a resize. */
  isResizeStart: boolean;
  /** True when a resize was in progress before these changes. */
  wasResizing: boolean;
  /** True when a resize is in progress after these changes. */
  isResizing: boolean;
}

/**
 * Follows the drag and resize gestures in a stream of React Flow node
 * changes, for a diagram that keeps its phases apart with `cascadePhases`.
 *
 * A drag sends `position` changes with `dragging: true`, and ends with
 * `dragging: false`. A resize sends `dimensions` changes with
 * `resizing: true`, and ends with `resizing: false`. The nodes from the first
 * frame of a gesture stay the baseline until the gesture ends, so a phase that
 * a gesture pushes aside goes back when the gesture goes back.
 *
 * Call the returned function once for each batch of changes, with the nodes
 * before the changes.
 */
export function usePhaseGesture<TNode extends Node>() {
  const baselineRef = useRef<TNode[] | null>(null);
  const isResizingRef = useRef(false);

  return useCallback(
    (
      changes: NodeChange<TNode>[],
      currentNodes: TNode[]
    ): PhaseGestureFrame<TNode> => {
      const isDragFrame = changes.some(
        (change) => change.type === 'position' && change.dragging === true
      );
      const isDragEnd = changes.some(
        (change) => change.type === 'position' && change.dragging === false
      );
      // Only `dimensions` changes with a `resizing` flag come from a resize.
      // Measurement changes have no flag.
      const isResizeFrame = changes.some(
        (change) => change.type === 'dimensions' && change.resizing === true
      );
      const isResizeEnd = changes.some(
        (change) => change.type === 'dimensions' && change.resizing === false
      );
      const wasResizing = isResizingRef.current;
      const isResizeStart = isResizeFrame && !wasResizing;

      if (isDragFrame && !baselineRef.current) {
        baselineRef.current = currentNodes;
      }
      if (isResizeStart) {
        baselineRef.current = currentNodes;
      }
      if (isResizeFrame) isResizingRef.current = true;
      // The last resize frame has the final size of the previous frame, so it
      // measures against the nodes before it.
      if (isResizeEnd) {
        isResizingRef.current = false;
        baselineRef.current = null;
      }

      const baselineNodes = baselineRef.current ?? currentNodes;
      // The last drag frame can move the node, so it measures against the
      // baseline of the drag.
      if (isDragEnd) baselineRef.current = null;

      return {
        baselineNodes,
        canMovePhases:
          isResizeFrame ||
          isResizeEnd ||
          changes.some(
            (change) => change.type === 'position' || change.type === 'replace'
          ),
        isResizeStart,
        wasResizing,
        isResizing: isResizingRef.current,
      };
    },
    []
  );
}
